/**
 * Post-Round-96 — Feature flag hook.
 *
 * Fetches the customer-visible feature flag state from
 * `GET /api/paygbot/feature-flags` once per app mount, caches it
 * in module-scope, and returns a synchronous ``{[flag]: bool}``
 * dict.
 *
 * Fail-safe: on network error, returns all-off so pre-flag
 * behaviour prevails.
 *
 * Usage:
 *
 *   const flags = useFeatureFlags();
 *   if (flags.execution_loop_v1) { ... }
 */
import { useEffect, useState } from 'react';
import api from '@/utils/api';

let _cache = null;      // resolved flag map, once fetched
let _inflight = null;   // shared promise so concurrent hooks reuse

async function fetchFlags() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get('/paygbot/feature-flags')
    .then((resp) => {
      _cache = resp?.data || {};
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export default function useFeatureFlags() {
  const [flags, setFlags] = useState(_cache || {});
  useEffect(() => {
    if (_cache) {
      setFlags(_cache);
      return;
    }
    let alive = true;
    fetchFlags().then((f) => { if (alive) setFlags(f); });
    return () => { alive = false; };
  }, []);
  return flags;
}
